import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getFunctions, Functions } from 'firebase/functions';
import { getStorage, FirebaseStorage } from 'firebase/storage';

/**
 * ForgivePath Firebase Configuration
 * 
 * CRITICAL FOR PRODUCTION:
 * Vite replaces 'import.meta.env.VITE_...' strings with their actual values 
 * DURING THE BUILD process. We use a safety guard to prevent crashes 
 * if the environment object itself is missing (e.g., non-Vite context).
 */

const getSafeEnv = (): any => {
  try {
    // We access it this way to ensure the bundler can still find the literal strings for replacement
    return (typeof import.meta !== 'undefined' && (import.meta as any).env) || {};
  } catch (e) {
    return {};
  }
};

const env = getSafeEnv();

const firebaseConfig = {
  apiKey: (env.VITE_FIREBASE_API_KEY || '').trim(),
  authDomain: (env.VITE_FIREBASE_AUTH_DOMAIN || '').trim(),
  projectId: (env.VITE_FIREBASE_PROJECT_ID || '').trim(),
  storageBucket: (env.VITE_FIREBASE_STORAGE_BUCKET || '').trim(),
  messagingSenderId: (env.VITE_FIREBASE_MESSAGING_SENDER_ID || '').trim(),
  appId: (env.VITE_FIREBASE_APP_ID || '').trim()
};

// --- INITIALIZATION ---
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let functions: Functions;
let storage: FirebaseStorage;
const googleProvider = new GoogleAuthProvider();

// Validation: Check if the key exists and is valid (Firebase keys start with 'AIza')
const isKeyPresent = !!firebaseConfig.apiKey && firebaseConfig.apiKey.startsWith('AIza');

if (isKeyPresent) {
  try {
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }
  } catch (error) {
    console.error("[FIREBASE] Initialization error:", error);
    // Fallback to prevent white-screen crashes
    app = initializeApp({ apiKey: "invalid", appId: "invalid", projectId: "invalid" });
  }
} else {
  // Helpful production logging for deployment troubleshooting
  console.warn(`[CONFIG WARNING] Firebase API Key is missing or invalid. 
Check your environment variables (e.g., Vercel Dashboard).
Value detected: "${firebaseConfig.apiKey}"
If you just added these variables, you MUST trigger a new deployment for them to take effect.`);

  // Fallback app to prevent 'auth' and 'db' from being undefined
  app = initializeApp({
    apiKey: "missing",
    authDomain: "missing",
    projectId: "forgivepath-8a2ed",
    storageBucket: "missing",
    messagingSenderId: "missing",
    appId: "missing"
  });
}

const isFirebaseConfigured = isKeyPresent;

// Initialize services
auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(err => console.error("Persistence error:", err));
db = getFirestore(app);
functions = getFunctions(app);
storage = getStorage(app);

export { auth, db, functions, storage, googleProvider, isFirebaseConfigured };